import React from "react";
import CheckIcon from "@mui/icons-material/Check";

/*
  Progress rail for the RFQ intake wizard.

  Steps the user has already satisfied stay clickable — going back to swap a
  customer or nudge the margin shouldn't mean restarting the flow. Steps that
  aren't reachable yet render disabled rather than hidden so the shape of the
  whole flow is visible from step one.

  Each step: { label, summary, complete, reachable }
*/
const StepRail = ({ steps, currentStep, onStepSelect }) => {
  return (
    <nav
      aria-label="RFQ progress"
      className="app-card rounded-2xl border p-3 sm:p-4"
    >
      <ol className="flex items-center">
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const isCurrent = stepNumber === currentStep;
          // Keep the number visible on the step you're standing on, even once
          // it's satisfied — the checkmark is for steps you've moved past.
          const showCheck = step.complete && !isCurrent;

          return (
            <React.Fragment key={step.label}>
              <li className="min-w-0">
                <button
                  type="button"
                  disabled={!step.reachable}
                  aria-current={isCurrent ? "step" : undefined}
                  onClick={() => onStepSelect(stepNumber)}
                  aria-label={`Step ${stepNumber}: ${step.label}`}
                  className={`flex min-h-[2.75rem] min-w-0 items-center gap-2.5 rounded-xl px-2 py-1.5 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 sm:px-3 ${
                    step.reachable
                      ? "hover:bg-[color:var(--app-hover)]"
                      : "cursor-not-allowed opacity-50"
                  }`}
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold ${
                      showCheck
                        ? "border-sky-600 bg-sky-600 text-white"
                        : isCurrent
                        ? "app-accent border-sky-500 bg-sky-500/10"
                        : "app-surface app-muted border"
                    }`}
                  >
                    {showCheck ? (
                      <CheckIcon sx={{ fontSize: 18 }} />
                    ) : (
                      stepNumber
                    )}
                  </span>

                  {/* Labels are the first thing to go on a narrow screen; the
                      numbered circles alone still convey position. */}
                  <span className="hidden min-w-0 sm:block">
                    <span
                      className={`block truncate text-sm font-semibold leading-tight ${
                        isCurrent ? "app-accent" : ""
                      }`}
                    >
                      {step.label}
                    </span>
                    <span className="app-muted block truncate text-xs">
                      {step.summary}
                    </span>
                  </span>
                </button>
              </li>

              {index < steps.length - 1 && (
                <li
                  aria-hidden="true"
                  className="mx-1 h-1 flex-1 overflow-hidden rounded-full bg-slate-400/25 sm:mx-2"
                >
                  <div
                    className={`h-full rounded-full bg-sky-500 transition-all duration-300 ${
                      step.complete ? "w-full" : "w-0"
                    }`}
                  />
                </li>
              )}
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

export default StepRail;
